"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { Plus, LogOut, User, LifeBuoy, ChevronDown, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/theme-toggle"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
// Use Clerk for auth
import { useAuth, SignOutButton } from '@clerk/nextjs';
import Image from "next/image"
import { useTheme } from "next-themes"
import { UserButton } from "@clerk/nextjs"
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "@/components/ui/resizable-navbar";

export function LandingHeader() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  // Use Clerk auth state
  const { isSignedIn } = useAuth();
  const router = useRouter()
  const pathname = usePathname()
  const { theme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const isLandingPage = pathname === '/'

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSignUp = () => {
    router.push('/register');
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const handleGoToChat = () => {
    router.push('/chat');
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  }

  // Define the most important navigation items
  const navItems = [
    {
      name: "Features",
      link: "#features",
    },
    {
      name: "Providers",
      link: "#who-its-for",
    },
    {
      name: "Testimonials",
      link: "#what-users-say",
    },
  ];

  return (
    <Navbar className="z-50 border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-[#0F172A]/95 backdrop-blur-sm">
      {/* Desktop Navigation */}
      <NavBody>
        <NavbarLogo />
        <NavItems items={navItems} />
        <div className="flex items-center gap-4 z-20">
          {isSignedIn ? (
            <>
              <NavbarButton
                onClick={handleGoToChat}
                variant="primary"
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                <span>New Chat</span>
              </NavbarButton>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1 text-foreground dark:text-foreground">
                    <User className="h-4 w-4" />
                    <span>Account</span> 
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="cursor-pointer" asChild>
                    <SignOutButton redirectUrl="/">
                      <button className="flex items-center w-full">
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Sign out</span>
                      </button>
                    </SignOutButton>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <NavbarButton
                href="/register"
                variant="primary"
                className="flex items-center gap-1"
              >
                Try It Now
              </NavbarButton>
            </>
          )}
          <ThemeToggle />
        </div>
      </NavBody>

      {/* Mobile Navigation */}
      <MobileNav>
        <MobileNavHeader>
          <NavbarLogo />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </div>
        </MobileNavHeader>

        <MobileNavMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        >
          {navItems.map((item, idx) => (
            <a
              key={`mobile-link-${idx}`}
              href={item.link}
              onClick={() => setIsMobileMenuOpen(false)}
              className="relative text-neutral-600 dark:text-neutral-300 py-2"
            >
              <span className="block">{item.name}</span>
            </a>
          ))}
          {isSignedIn ? (
            <div className="flex w-full flex-col gap-4 mt-4">
              <NavbarButton
                onClick={handleGoToChat}
                variant="primary"
                className="w-full flex items-center gap-2 justify-center"
              >
                <Plus className="h-4 w-4" />
                <span>New Chat</span>
              </NavbarButton>
              <SignOutButton redirectUrl="/">
                <NavbarButton
                  variant="secondary"
                  className="w-full flex items-center gap-2 justify-center"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </NavbarButton>
              </SignOutButton>
            </div>
          ) : (
            <div className="flex w-full flex-col gap-4 mt-4">
              <NavbarButton
                href="/register"
                variant="primary"
                className="w-full flex items-center gap-2 justify-center"
              >
                <span>Try It Now</span>
                <ChevronRight className="h-4 w-4" />
              </NavbarButton>
            </div>
          )}
        </MobileNavMenu>
      </MobileNav>
    </Navbar>
  )
} 